import { useMemo, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Package,
  Pause,
  PanelLeftClose,
  PanelLeftOpen,
  Route,
  UserPlus,
  Users,
} from "lucide-react";

import {
  type CarrierOperator,
  type CarrierTask,
  type CarrierTrip,
  useRemoveTripStop,
} from "@/api/carrier";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";

import { ReadinessChip } from "@/features/dispatch/readiness-chip";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { ModalStopList, RemoveStopDialog } from "./modal-stops";
import { buildStops, type DerivedStop } from "./trip-stops";
import { isTripRoutingEditable } from "./trip-routing-state";
import {
  dispatchCapacityLoad,
  dispatchTaskScheduleLabel,
  isDispatchTaskOverdue,
} from "./dispatch-read-model";
import { TripActionsMenu } from "./trip-actions-menu";
import { DispatchInlineState } from "./dispatch-state";
import { AwaitingReceiptSection } from "./awaiting-receipt-section";
import { canFailTask, type TaskFailureTarget } from "./task-failure";
import { taskDispatchCenterId } from "./task-staging";
import {
  formatEta,
  labelize,
  operatorInitials,
  operatorName,
  StatusPill,
  taskLabel,
  taskLocation,
  tripLabel,
} from "./dispatch-presenters";

function DriverCard({
  op,
  trips,
  onOpenTrip,
  onAssignDriver,
}: {
  op: CarrierOperator;
  trips: CarrierTrip[];
  onOpenTrip: (trip: CarrierTrip) => void;
  onAssignDriver: (trip: CarrierTrip) => void;
}) {
  const [open, setOpen] = useState(false);
  const rolling = op.status === "ON_DUTY" && Boolean(op.lastLocationTime);
  const dotColor =
    op.status === "ON_DUTY"
      ? "#16a34a"
      : op.status === "BLOCKED"
        ? "#dc2626"
        : "#a1a1aa";

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${operatorName(op)}, ${trips.length} trip${trips.length === 1 ? "" : "s"}`}
        className="flex min-h-11 w-full items-center gap-2.5 p-2.5 text-left hover:bg-zinc-50"
        onClick={() => setOpen((value) => !value)}
      >
        <div className="relative shrink-0">
          <Avatar className="h-9 w-9 border border-white bg-indigo-600 text-xs font-bold text-white">
            <AvatarFallback className="bg-indigo-600 text-white">
              {operatorInitials(op)}
            </AvatarFallback>
          </Avatar>
          <span
            className={cn(
              "absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-white",
              rolling && "animate-pulse"
            )}
            style={{ background: dotColor }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-zinc-900">
            {operatorName(op)}
          </div>
          <div className="truncate text-xs text-zinc-500">
            {op.vehicle?.code ?? op.vehicle?.licensePlate ?? "No vehicle"} ·{" "}
            {rolling ? "Rolling" : labelize(op.status)}
          </div>
        </div>
        <div className="text-right text-[11px] text-zinc-500">
          <div className="font-semibold text-zinc-700">
            {trips.length} trip{trips.length === 1 ? "" : "s"}
          </div>
          <div>
            GPS{" "}
            {op.lastLocationTime
              ? formatRelativeTime(op.lastLocationTime)
              : "unavailable"}
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-zinc-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t border-zinc-100 bg-zinc-50/60 p-2">
          {trips.length === 0 ? (
            <p className="px-2 py-1 text-xs text-zinc-500">No trips</p>
          ) : (
            trips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onOpenTrip={onOpenTrip}
                onAssignDriver={onAssignDriver}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TripCard({
  trip,
  onOpenTrip,
  onAssignDriver,
}: {
  trip: CarrierTrip;
  onOpenTrip: (trip: CarrierTrip) => void;
  onAssignDriver: (trip: CarrierTrip) => void;
}) {
  const [open, setOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<DerivedStop | null>(null);
  const removeStop = useRemoveTripStop();
  const stops = useMemo(
    () => buildStops(trip.waypoints ?? []),
    [trip.waypoints]
  );
  const totalTasks = trip.tasks?.length ?? trip.totalTasks ?? 0;
  const stopCount = trip.waypoints?.length ?? 0;
  const capacity = dispatchCapacityLoad(trip);
  const firstEta = formatEta(trip.waypoints?.[0]?.eta?.estimatedArrivalTime);
  const capacityPercent = capacity
    ? Math.min(100, Math.round((capacity.load / capacity.capacity) * 100))
    : 0;

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div className="flex items-start gap-2 p-2">
        <span
          className="self-stretch rounded-full"
          style={{ width: 4, background: trip.color ?? "#4f46e5" }}
        />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start gap-1">
            <button
              type="button"
              className="flex min-h-11 flex-1 items-center gap-1.5 text-left lg:min-h-0"
              onClick={() => setOpen((value) => !value)}
            >
              <span className="whitespace-nowrap font-mono text-xs font-bold text-zinc-900">
                {tripLabel(trip)}
              </span>
              <StatusPill status={trip.status} />
              <span className="ml-auto whitespace-nowrap text-[11px] text-zinc-500">
                {stopCount} stop{stopCount === 1 ? "" : "s"} · {totalTasks} task
                {totalTasks === 1 ? "" : "s"}
              </span>
            </button>
            <div className="flex shrink-0 items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm-touch"
                aria-label={`Focus ${tripLabel(trip)}`}
                onClick={() => onOpenTrip(trip)}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm-touch"
                aria-label={`${open ? "Collapse" : "Expand"} ${tripLabel(trip)}`}
                onClick={() => setOpen((value) => !value)}
              >
                {open ? (
                  <ChevronUp className="h-4 w-4 text-zinc-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-zinc-400" />
                )}
              </Button>
              <TripActionsMenu trip={trip} />
            </div>
          </div>

          {(capacity || firstEta) && (
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
              {capacity && (
                <>
                  <span className="font-medium text-zinc-700">Capacity</span>
                  <span
                    role="progressbar"
                    aria-label="Capacity load"
                    aria-valuemin={0}
                    aria-valuemax={capacity.capacity}
                    aria-valuenow={capacity.load}
                    className="h-1.5 min-w-12 flex-1 overflow-hidden rounded-full bg-zinc-200"
                  >
                    <span
                      className="block h-full rounded-full bg-indigo-600"
                      style={{ width: `${capacityPercent}%` }}
                    />
                  </span>
                  <span className="font-mono tabular-nums">
                    {capacity.load}/{capacity.capacity} {capacity.unit}
                  </span>
                </>
              )}
              {firstEta && (
                <span className="ml-auto whitespace-nowrap font-mono tabular-nums">
                  ETA {firstEta}
                </span>
              )}
            </div>
          )}

          <ReadinessChip trip={trip} waypointCount={stopCount} />

          {!trip.operatorId && isTripRoutingEditable(trip.status) && (
            <Button
              type="button"
              size="sm"
              className="h-8 w-full gap-1.5 text-[12px]"
              onClick={() => onAssignDriver(trip)}
            >
              <UserPlus className="h-3.5 w-3.5" /> Assign driver
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className="space-y-1 border-t border-zinc-100 bg-zinc-50/60 p-2">
          {(trip.waypoints ?? []).length === 0 ? (
            <p className="px-1 py-1 text-xs text-zinc-500">No stops yet</p>
          ) : (
            <ModalStopList
              stops={stops}
              color={trip.color ?? "#4f46e5"}
              selectedId={null}
              armed={false}
              busy={removeStop.isPending}
              canStart={false}
              canRemove={isTripRoutingEditable(trip.status)}
              onInspect={() => onOpenTrip(trip)}
              onStart={() => onOpenTrip(trip)}
              onRemove={setRemoveTarget}
              onReorder={async () => {
                // Sequencing happens in Focus, where the map is.
              }}
            />
          )}
        </div>
      )}

      <RemoveStopDialog
        stop={removeTarget}
        pending={removeStop.isPending}
        onOpenChange={(next) => !next && setRemoveTarget(null)}
        onConfirm={async () => {
          if (!removeTarget) return;
          try {
            await removeStop.mutateAsync({
              tripId: trip.id,
              waypointId: removeTarget.id,
            });
            toast.success("Stop removed from Trip.");
          } catch {
            toast.error("Stop could not be removed.");
          } finally {
            setRemoveTarget(null);
          }
        }}
      />
    </div>
  );
}

/** One figure in the collapsed rail: an icon, a number, and a real label. */
function CollapsedStat({
  icon,
  value,
  label,
  tone = "zinc",
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  tone?: "zinc" | "indigo" | "amber";
}) {
  return (
    <div
      title={label}
      aria-label={label}
      className={cn(
        "flex w-11 flex-col items-center gap-0.5 rounded-md py-1.5",
        tone === "indigo" && "bg-indigo-50 text-indigo-700",
        tone === "amber" && "bg-amber-50 text-amber-700",
        tone === "zinc" && "text-zinc-500"
      )}
    >
      {icon}
      <span className="font-mono text-[11px] font-bold tabular-nums">
        {value}
      </span>
    </div>
  );
}

function TaskCard({
  task,
  onAssign,
  onFail,
  selected,
  unavailable,
}: {
  task: CarrierTask;
  onAssign?: (task: CarrierTask) => void;
  onFail?: (task: TaskFailureTarget) => void;
  selected: boolean;
  unavailable: boolean;
}) {
  const held = isTaskOnCancellationHold(task);
  const scheduleLabel = dispatchTaskScheduleLabel(task);
  const overdue = isDispatchTaskOverdue(task);
  return (
    <div
      className={cn(
        "rounded-lg border border-zinc-200 p-3 shadow-sm",
        held ? "bg-zinc-100 text-zinc-500" : "bg-white"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-2">
            <TaskReferenceDisplay task={task} className="flex-1" />
            <StatusPill status={task.status} />
            {held && (
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
                <Pause className="h-2.5 w-2.5" /> On hold
              </span>
            )}
          </div>
          <div className="mt-1 truncate text-sm font-medium text-zinc-900">
            {labelize(task.type)}
          </div>
          <div className="mt-1 truncate text-xs text-zinc-500">
            {taskLocation(task)}
          </div>
          <div
            className={cn(
              "mt-1 font-mono text-[11px] font-medium tabular-nums",
              overdue ? "text-red-700" : "text-amber-700"
            )}
          >
            {scheduleLabel}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onFail && !held && canFailTask(task.status, task.type) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 px-3 text-xs text-red-700 hover:bg-red-50 hover:text-red-800 lg:h-7 lg:px-2"
              aria-label={`Fail ${taskLabel(task)}`}
              onClick={() => onFail(task)}
            >
              Fail
            </Button>
          )}
          {onAssign && (
            <Button
              type="button"
              variant={selected ? "secondary" : "outline"}
              size="sm"
              className="h-11 px-3 text-xs lg:h-7 lg:px-2"
              aria-label={`${selected ? "Remove" : held ? "On hold" : unavailable ? "Unavailable" : "Stage"} ${taskLabel(task)}`}
              disabled={unavailable || held}
              onClick={() => onAssign(task)}
            >
              {selected
                ? "Selected"
                : held
                  ? "Cancellation hold"
                  : unavailable
                    ? "Center locked"
                    : "Stage"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function OperatorRail({
  operators,
  trips,
  unassignedTasks,
  awaitingReceiptTasks,
  isLoading,
  onToggleCollapsed,
  onOpenTrip,
  onAssignDriver,
  onAssignTask,
  onFailTask,
  stagingCenterId,
  stagedTaskIds,
  initialTab = "fleet",
  activeTab,
  onTabChange,
  showHeader = true,
  collapsible = true,
  collapsed = false,
}: {
  collapsed?: boolean;
  operators: CarrierOperator[];
  trips: CarrierTrip[];
  unassignedTasks: CarrierTask[];
  awaitingReceiptTasks: CarrierTask[];
  isLoading: boolean;
  onToggleCollapsed?: () => void;
  onOpenTrip: (trip: CarrierTrip) => void;
  onAssignDriver: (trip: CarrierTrip) => void;
  onAssignTask: (task: CarrierTask) => void;
  onFailTask: (task: TaskFailureTarget) => void;
  stagingCenterId: string | null;
  stagedTaskIds: Set<string>;
  initialTab?: "fleet" | "tasks";
  activeTab?: "fleet" | "tasks";
  onTabChange?: (tab: "fleet" | "tasks") => void;
  showHeader?: boolean;
  collapsible?: boolean;
}) {
  const unassignedTrips = trips.filter((trip) => !trip.operatorId);
  const assignedTrips = trips.filter((trip) => trip.operatorId);

  const onDutyCount = operators.filter(
    (operator) => operator.status === "ON_DUTY"
  ).length;
  const activeTripCount = trips.filter(
    (trip) => trip.status === "ACTIVE"
  ).length;
  const unassignedCount = unassignedTasks.length;

  if (collapsed && collapsible) {
    return (
      <div className="flex h-full flex-col items-center gap-2 p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Expand rail"
          onClick={() => onToggleCollapsed?.()}
        >
          <PanelLeftOpen className="h-4 w-4" />
        </Button>
        {/*
          A collapsed rail used to show two bare numbers. A number with no
          noun is not a summary — it is a puzzle. These carry their icon and
          their meaning, and the figures are the ones worth interrupting for:
          drivers actually on duty, trips already moving, and work nobody has
          picked up.
        */}
        <CollapsedStat
          icon={<Users className="h-3.5 w-3.5" />}
          value={`${onDutyCount}/${operators.length}`}
          label={`${onDutyCount} of ${operators.length} drivers on duty`}
        />
        <CollapsedStat
          icon={<Route className="h-3.5 w-3.5" />}
          value={activeTripCount}
          label={`${activeTripCount} active ${activeTripCount === 1 ? "trip" : "trips"}`}
          tone={activeTripCount > 0 ? "indigo" : "zinc"}
        />
        <CollapsedStat
          icon={<Package className="h-3.5 w-3.5" />}
          value={unassignedCount}
          label={`${unassignedCount} unassigned ${unassignedCount === 1 ? "task" : "tasks"}`}
          tone={unassignedCount > 0 ? "amber" : "zinc"}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showHeader && (
        <div className="flex h-12 items-center justify-between border-b border-zinc-200 px-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            Fleet
            <Badge variant="secondary">{operators.length}</Badge>
          </div>
          {collapsible && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Collapse rail"
              onClick={() => onToggleCollapsed?.()}
            >
              <PanelLeftClose className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
      {isLoading ? (
        <DispatchInlineState title="Loading dispatch data..." loading />
      ) : (
        <Tabs
          defaultValue={initialTab}
          value={activeTab}
          onValueChange={(value) => onTabChange?.(value as "fleet" | "tasks")}
          className="flex min-h-0 flex-1 flex-col gap-0"
        >
          <TabsList
            size={collapsible ? "default" : "touch"}
            className="mx-3 mt-3 grid w-auto grid-cols-2 max-lg:h-11"
          >
            <TabsTrigger value="fleet">Fleet</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
          </TabsList>

          <TabsContent value="fleet" className="min-h-0 overflow-y-auto p-3">
            <div className="space-y-2.5">
              {unassignedTrips.length > 0 && (
                <section className="space-y-2 rounded-lg border border-orange-200 bg-orange-50 p-2">
                  <div className="flex items-center gap-2 px-1 text-xs font-bold text-zinc-900">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <span>Unassigned trips</span>
                    <Badge
                      variant="secondary"
                      className="ml-auto bg-orange-100 text-orange-700"
                    >
                      {unassignedTrips.length}
                    </Badge>
                  </div>
                  {unassignedTrips.map((trip) => (
                    <TripCard
                      key={trip.id}
                      trip={trip}
                      onOpenTrip={onOpenTrip}
                      onAssignDriver={onAssignDriver}
                    />
                  ))}
                </section>
              )}
              {operators.length === 0 && unassignedTrips.length === 0 && (
                <DispatchInlineState title="No Fleet data in this Dispatch Center." />
              )}
              {operators.map((op) => (
                <DriverCard
                  key={op.id}
                  op={op}
                  trips={assignedTrips.filter(
                    (trip) => trip.operatorId === op.id
                  )}
                  onOpenTrip={onOpenTrip}
                  onAssignDriver={onAssignDriver}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="min-h-0 overflow-y-auto p-3">
            <div className="space-y-4">
              <TaskSection
                title="Unassigned"
                tasks={unassignedTasks}
                empty="No unassigned tasks."
                onAssign={onAssignTask}
                onFail={onFailTask}
                stagingCenterId={stagingCenterId}
                stagedTaskIds={stagedTaskIds}
              />
              <AwaitingReceiptSection tasks={awaitingReceiptTasks} />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function TaskSection({
  title,
  tasks,
  empty,
  onAssign,
  onFail,
  stagingCenterId,
  stagedTaskIds,
}: {
  title: string;
  tasks: CarrierTask[];
  empty: string;
  onAssign?: (task: CarrierTask) => void;
  onFail?: (task: TaskFailureTarget) => void;
  stagingCenterId?: string | null;
  stagedTaskIds?: Set<string>;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between text-sm font-semibold text-zinc-800">
        <span>{title}</span>
        <Badge variant="secondary">{tasks.length}</Badge>
      </div>
      {tasks.length === 0 ? (
        <DispatchInlineState title={empty} />
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onAssign={onAssign}
              onFail={onFail}
              selected={stagedTaskIds?.has(task.id) ?? false}
              unavailable={Boolean(
                isTaskOnCancellationHold(task) ||
                  (stagingCenterId &&
                    taskDispatchCenterId(task) !== stagingCenterId)
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}
